import { dropdownMenuContainer } from '@/utils/portalContainer'
import { css } from '@emotion/react'
import { Popover, type PopoverProps } from '@mantine/core'
import { isEmpty } from 'lodash-es'
import { AnimatePresence } from 'motion/react'
import {
	CSSProperties,
	type ForwardedRef,
	type HTMLProps,
	type MouseEvent,
	type ReactNode,
	forwardRef,
	useImperativeHandle,
	useState,
} from 'react'
import MenuItem from './Item'
import type { MenuItemData } from './types'

export interface DropdownMenuProps {
	renderTrigger: (props: HTMLProps<HTMLElement>) => ReactNode
	data: MenuItemData[]
	onSelect: (id: MenuItemData['id'], data: MenuItemData, e: MouseEvent<HTMLElement>) => void
	dropdownStyles?: CSSProperties
	position?: PopoverProps['position']
}

export interface DropdownMenuHandle {
	isOpen: boolean
	openPortal: () => void
	closePortal: () => void
	moveToNext: () => void
	moveToPrevious: () => void
	activeItem: MenuItemData | null
}

function DropdownMenu(
	{ renderTrigger, data, onSelect, dropdownStyles, position = 'top-start' }: DropdownMenuProps,
	ref: ForwardedRef<DropdownMenuHandle>
) {
	const [isOpen, setIsOpen] = useState(false)
	const [activeId, setActiveId] = useState<string | null>(null)

	useImperativeHandle(
		ref,
		() => ({
			isOpen,
			openPortal: () => setIsOpen(true),
			closePortal: () => setIsOpen(false),
			moveToNext: () => {
				if (!activeId) {
					setActiveId(data[0]?.id)
					return
				}
				const currentIndex = data.findIndex((item) => item.id === activeId)
				if (currentIndex < data.length - 1) {
					setActiveId(data[currentIndex + 1].id)
				}
			},
			moveToPrevious: () => {
				if (!activeId) {
					setActiveId(data[data.length - 1]?.id)
					return
				}
				const currentIndex = data.findIndex((item) => item.id === activeId)
				if (currentIndex > 0) {
					setActiveId(data[currentIndex - 1].id)
				}
			},
			activeItem: data.find((item) => item.id === activeId) || null,
		}),
		[activeId, data, isOpen]
	)

	if (isEmpty(data)) {
		return renderTrigger({})
	}

	return (
		<Popover
			opened={isOpen}
			onChange={setIsOpen}
			position={position}
			offset={4}
			width={160}
			floatingStrategy="absolute"
			unstyled
			styles={{
				dropdown: {
					width: '160px',
					padding: '4px',
					borderRadius: '10px',
					background: 'var(--bg-on-body-1, #fff)',
					fontSize: '14px',
					lineHeight: '20px',
					boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.24)',
					position: 'absolute',
					...dropdownStyles,
				},
			}}
			portalProps={{
				target: dropdownMenuContainer,
			}}
		>
			<Popover.Target>
				{renderTrigger({
					onClick: () => setIsOpen(!isOpen),
					'aria-expanded': isOpen,
					'aria-haspopup': true,
					role: 'button',
					tabIndex: 0,
				})}
			</Popover.Target>

			<Popover.Dropdown>
				<div
					role="menu"
					css={css`
						display: flex;
						flex-direction: column;
						gap: 2px;
					`}
				>
					<AnimatePresence mode="popLayout">
						{data.map((item, index) => (
							<MenuItem
								{...item.props}
								key={item.id}
								data={item}
								onClick={(id, data, e) => {
									e.stopPropagation()
									onSelect(id, data, e)
									setIsOpen(false)
								}}
								isActive={item.id === activeId}
								initial={{
									x: -10,
									opacity: 0,
								}}
								animate={{
									x: 0,
									opacity: 1,
									transition: {
										delay: index * 0.04,
										damping: 20,
									},
								}}
								exit={{
									x: 10,
									opacity: 0,
								}}
							/>
						))}
					</AnimatePresence>
				</div>
			</Popover.Dropdown>
		</Popover>
	)
}

export default forwardRef<DropdownMenuHandle, DropdownMenuProps>(DropdownMenu)
